import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import { clearModuleCache } from '@/lib/hasModuleAccess';
import SuperAdminLayout from '@/components/feature/SuperAdminLayout';
import SAHeader from '@/components/feature/SAHeader';
import LoadingState from '@/components/base/LoadingState';
import ErrorState from '@/components/base/ErrorState';
import { supabase } from '@/lib/supabaseClient';
import { requireSuperAdmin } from '@/middleware/superAdminGuard';
import { getAllTenantsStorageMap } from '@/lib/getStorageUsage';
import OverviewTab from './tabs/OverviewTab';
import ModulesTab from './tabs/ModulesTab';
import BillingTab from './tabs/BillingTab';
import StorageTab from './tabs/StorageTab';
import DomainTab from './tabs/DomainTab';
import UsersTab from './tabs/UsersTab';

// ── Types ─────────────────────────────────────────────────────────────────────
export interface TenantInfo {
  id: string; name: string; slug: string; plan_code: string; status: string;
  max_users: number; max_active_contracts: number;
  storage_quota_mb: number; storage_used_mb: number;
  custom_domain: string | null; custom_domain_enabled: boolean;
  custom_domain_status: string; created_at: string; updated_at: string;
}
export interface SaasModule  { id: string; name: string; category: string; is_core: boolean; price_monthly: number; }
export interface TenantModuleRow { tenant_id: string; module_id: string; is_enabled: boolean; source: string; price_monthly: number; }
export interface TenantUser { id: string; user_id: string; role: string; created_at: string; }
export interface Counts { clients: number; contracts: number; jobs: number; payments: number; leads: number; }

// ── Tabs ──────────────────────────────────────────────────────────────────────
type TabId = 'overview' | 'modules' | 'billing' | 'storage' | 'domain' | 'users';

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: 'overview', label: 'Overview',  icon: 'ri-layout-grid-line' },
  { id: 'modules',  label: 'Modules',   icon: 'ri-puzzle-line' },
  { id: 'billing',  label: 'Billing',   icon: 'ri-coins-line' },
  { id: 'storage',  label: 'Storage',   icon: 'ri-hard-drive-line' },
  { id: 'domain',   label: 'Domain',    icon: 'ri-global-line' },
  { id: 'users',    label: 'Users',     icon: 'ri-team-line' },
];

// ── Page ──────────────────────────────────────────────────────────────────────
export default function TenantDetailPage() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant]         = useState<TenantInfo | null>(null);
  const [counts, setCounts]         = useState<Counts>({ clients: 0, contracts: 0, jobs: 0, payments: 0, leads: 0 });
  const [storageBytes, setStorageBytes] = useState(0);
  const [modules, setModules]       = useState<SaasModule[]>([]);
  const [planSet, setPlanSet]       = useState<Set<string>>(new Set());
  const [tmMap, setTmMap]           = useState<Record<string, TenantModuleRow>>({});
  const [users, setUsers]           = useState<TenantUser[]>([]);
  const [planPrice, setPlanPrice]   = useState(0);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState<string | null>(null);
  const [activeTab, setActiveTab]   = useState<TabId>('overview');

  // ── Load ────────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    if (!tenantId) return;
    setError(null);
    const { data: tData, error: tErr } = await supabase
      .from('saas_tenants').select('*').eq('id', tenantId).maybeSingle();
    if (tErr || !tData) { setError(tErr?.message ?? 'Tenant no encontrado.'); setLoading(false); return; }
    const t = tData as TenantInfo;
    setTenant(t);

    const [
      clientsRes, contractsRes, jobsRes, paymentsRes, leadsRes,
      storageMap, pmRes, tmRes, modulesRes, usersRes, planRes,
    ] = await Promise.all([
      supabase.from('clients').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      supabase.from('contracts').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      supabase.from('jobs').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      supabase.from('payments').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      supabase.from('marketing_leads').select('id', { count: 'exact', head: true }).eq('tenant_id', tenantId),
      getAllTenantsStorageMap(),
      supabase.from('saas_plan_modules').select('module_id').eq('plan_id', t.plan_code).eq('is_included', true),
      supabase.from('tenant_modules').select('*').eq('tenant_id', tenantId),
      supabase.from('saas_modules').select('id, name, category, is_core, price_monthly').order('category').order('name'),
      supabase.from('saas_tenant_users').select('id, user_id, role, created_at').eq('tenant_id', tenantId),
      supabase.from('saas_plans').select('price_monthly').eq('id', t.plan_code).maybeSingle(),
    ]);

    setCounts({
      clients: clientsRes.count ?? 0, contracts: contractsRes.count ?? 0,
      jobs: jobsRes.count ?? 0, payments: paymentsRes.count ?? 0, leads: leadsRes.count ?? 0,
    });
    setStorageBytes(storageMap[tenantId] ?? 0);

    const ps = new Set((pmRes.data ?? []).map((r: { module_id: string }) => r.module_id));
    const tm: Record<string, TenantModuleRow> = {};
    ((tmRes.data ?? []) as TenantModuleRow[]).forEach((r) => { tm[r.module_id] = r; });

    setModules((modulesRes.data ?? []) as SaasModule[]);
    setPlanSet(ps);
    setTmMap(tm);
    setUsers((usersRes.data ?? []) as TenantUser[]);
    setPlanPrice(Number(planRes.data?.price_monthly ?? 0));
    setLoading(false);
  }, [tenantId]);

  useEffect(() => {
    const init = async () => {
      const ok = await requireSuperAdmin();
      if (!ok) { navigate('/login'); return; }
      await load();
    };
    init();
  }, [navigate, load]);

  // ── Shared helpers ───────────────────────────────────────────────────────────
  const isEnabled = (moduleId: string) => tmMap[moduleId] ? tmMap[moduleId].is_enabled : planSet.has(moduleId);
  const getSource = (moduleId: string) => tmMap[moduleId] ? tmMap[moduleId].source : planSet.has(moduleId) ? 'plan' : '';
  const getPrice  = (mod: SaasModule) => tmMap[mod.id] ? tmMap[mod.id].price_monthly : planSet.has(mod.id) ? 0 : mod.price_monthly;

  const addonModules   = modules.filter((m) => !planSet.has(m.id) && isEnabled(m.id));
  const monthlyAddons  = addonModules.reduce((s, m) => s + getPrice(m), 0);

  // ── Guards ───────────────────────────────────────────────────────────────────
  if (loading) return (
    <SuperAdminLayout>
      <SAHeader title="Tenant Detail" subtitle="Cargando…" />
      <LoadingState />
    </SuperAdminLayout>
  );
  if (error || !tenant) return (
    <SuperAdminLayout>
      <SAHeader title="Tenant Detail" subtitle="" />
      <ErrorState message={error ?? 'Tenant no encontrado'} onRetry={load} />
    </SuperAdminLayout>
  );

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <SuperAdminLayout>
      <SAHeader
        title={tenant.name}
        subtitle={`${tenant.slug} · ${tenant.plan_code.toUpperCase()} · ${tenant.status}`}
        actions={
          <div className="flex items-center gap-2">
            {/* View as Tenant */}
            <button
              onClick={async () => {
                const result = await Swal.fire({
                  title: `View as "${tenant.name}"`,
                  html: `
                    <p style="color:#64748b;font-size:13px;margin-bottom:10px">Accederás al Admin Panel como si fueras este estudio.</p>
                    <div style="background:#f5f3ff;border:1px solid #ddd6fe;border-radius:8px;padding:10px 14px;text-align:left">
                      <p style="color:#5b21b6;font-size:12px;font-weight:600;margin:0 0 4px">Modo impersonación activo</p>
                      <p style="color:#7c3aed;font-size:12px;margin:0">Usarás el banner violeta en /admin para salir.</p>
                    </div>
                  `,
                  showCancelButton: true,
                  confirmButtonText: 'Entrar al Admin',
                  cancelButtonText: 'Cancelar',
                  confirmButtonColor: '#7c3aed',
                  background: '#ffffff', color: '#0f172a',
                });
                if (!result.isConfirmed) return;
                localStorage.setItem('impersonated_tenant_id',   tenant.id);
                localStorage.setItem('impersonated_tenant_name', tenant.name);
                localStorage.setItem('impersonated_tenant_slug', tenant.slug);
                localStorage.setItem('impersonated_from_sa',     'true');
                clearModuleCache();  // Flush cache for new tenant context
                navigate('/admin');
              }}
              className="flex items-center gap-2 px-3 py-2 text-sm text-white bg-violet-600 hover:bg-violet-700 rounded-lg transition-colors cursor-pointer whitespace-nowrap font-semibold"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-spy-line"></i></span>
              View as Tenant
            </button>

            {/* Back */}
            <Link
              to="/sa/tenants"
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-500 hover:text-slate-900 border border-slate-200 hover:border-slate-300 rounded-lg hover:bg-slate-50 transition-colors whitespace-nowrap"
            >
              <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-line"></i></span>
              Tenants
            </Link>
          </div>
        }
      />

      <div className="px-4 md:px-8 pt-5 pb-8">
        {/* ── Tab bar ── */}
        <div className="flex items-center gap-1 border-b border-slate-200 mb-6 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium whitespace-nowrap cursor-pointer border-b-2 -mb-px transition-all ${
                activeTab === tab.id
                  ? 'text-blue-600 border-blue-600'
                  : 'text-slate-500 border-transparent hover:text-slate-700 hover:border-slate-300'
              }`}
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className={`${tab.icon} text-sm`}></i>
              </span>
              {tab.label}
              {tab.id === 'modules' && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-blue-100 text-blue-700 text-[10px] font-bold inline-flex items-center justify-center">
                  {modules.filter((m) => isEnabled(m.id)).length}
                </span>
              )}
              {tab.id === 'users' && users.length > 0 && (
                <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold inline-flex items-center justify-center">
                  {users.length}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        {activeTab === 'overview' && (
          <OverviewTab
            tenant={tenant}
            counts={counts}
            storageBytes={storageBytes}
            onTenantChange={setTenant}
            onReload={load}
          />
        )}
        {activeTab === 'modules' && (
          <ModulesTab
            tenant={tenant}
            modules={modules}
            planSet={planSet}
            tmMap={tmMap}
            onTmMapChange={setTmMap}
          />
        )}
        {activeTab === 'billing' && (
          <BillingTab
            tenant={tenant}
            modules={modules}
            planSet={planSet}
            tmMap={tmMap}
            planPrice={planPrice}
            monthlyAddons={monthlyAddons}
            addonModules={addonModules}
            onTenantChange={setTenant}
            onReload={load}
          />
        )}
        {activeTab === 'storage' && (
          <StorageTab
            tenant={tenant}
            storageBytes={storageBytes}
            onTenantChange={setTenant}
          />
        )}
        {activeTab === 'domain' && (
          <DomainTab
            tenant={tenant}
            onTenantChange={setTenant}
          />
        )}
        {activeTab === 'users' && (
          <UsersTab
            tenant={tenant}
            users={users}
          />
        )}
      </div>
    </SuperAdminLayout>
  );
}
